/**
 * Account · Developers (immersive account view, tab 4 of 5).
 *
 * The technical surface of the account: API keys (masked, copy / revoke / create),
 * webhook endpoints, the connected IDs you build against, and a link to the API
 * reference. Re-skins the live DevelopersSection logic — the REAL useApiKeys hook
 * (create / revoke / list) — into the shared AcctPageHead + AcctCard rhythm the
 * other four tabs share. No business logic is re-implemented.
 *
 * Webhooks are not yet wired to a backend, so they render a purpose-built empty
 * state with a CTA rather than faked rows. Connected IDs come from the REAL
 * useAuth() team / user. Keys stay masked; copy writes to the clipboard.
 */
import { useState } from "react";
import { ArrowRight, Check, Code, Copy, Key, Loader2, Webhook } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { useApiKeys } from "@/hooks/useApiKeys";
import type { ApiKey, ApiKeyWithSecret } from "@/types/api-keys";
import {
  AcctButton,
  AcctCard,
  AcctPageHead,
  ACCT_BRAND as B,
} from "@/components/voice-account/VoiceAccountDetail";
import { DevelopersCreateKeySheet } from "./DevelopersCreateKeySheet";
import { DevelopersRevealKeySheet } from "./DevelopersRevealKeySheet";

const MONO =
  "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace";

const SECTION_LABEL =
  "text-xs font-bold tracking-[0.6px] uppercase text-gray-500";

const API_REFERENCE_URL = "https://helpgenie.mintlify.app";

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/** Production keys take the genie blue tint; everything else takes amber. */
const keyTint = (key: ApiKey): string =>
  /prod|live/i.test(key.name) ? B.green : B.amber;

const DevelopersTab = () => {
  const { user, team } = useAuth();
  const { apiKeys, isLoading, createKey, revokeKey } = useApiKeys();

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyWithSecret | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const activeKeys = apiKeys.filter((k) => k.is_active);

  /* ── re-skinned DevelopersSection handlers (real useApiKeys logic) ── */
  const handleCreate = async (name: string) => {
    setCreating(true);
    try {
      const key = await createKey(name.trim());
      setCreateOpen(false);
      setCreatedKey(key);
    } catch (err: any) {
      toast.error(err.message || "We couldn't create that key. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (
      !confirm(
        `Revoke "${key.name}"? Any requests using this key will stop working immediately.`
      )
    )
      return;
    setRevokingId(key.id);
    const loadingToast = toast.loading(`Revoking ${key.name}...`);
    try {
      await revokeKey(key.id);
      toast.success(`${key.name} has been revoked.`, { id: loadingToast });
    } catch (err: any) {
      toast.error(err.message || "We couldn't revoke that key.", {
        id: loadingToast,
      });
    } finally {
      setRevokingId(null);
    }
  };

  const copyValue = async (value: string, id: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      toast.success(`${label} copied to clipboard.`);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800);
    } catch {
      toast.error("We couldn't copy that. Please copy it manually.");
    }
  };

  /* ── connected IDs — real, non-secret display values ── */
  const connectedIds: { label: string; value: string; copyId: string }[] = [
    {
      label: "Workspace ID",
      value: team?.id || user?.id || "—",
      copyId: "workspace",
    },
    {
      label: "Voice ID",
      value: team?.id ? `voice_${team.id.slice(0, 8)}` : "—",
      copyId: "voice",
    },
  ];

  return (
    <>
      <AcctPageHead
        icon="code"
        title="Developers"
        subtitle="API keys, webhooks, and the IDs you'll need to build on Help Genie."
        tint={B.blue}
      />

      {/* ── API keys ── */}
      <AcctCard className="mb-5">
        <div className="flex items-center justify-between mb-3.5">
          <p className={SECTION_LABEL}>API keys</p>
          <AcctButton
            label="Create key"
            kind="tinted"
            icon="code"
            onClick={() => setCreateOpen(true)}
          />
        </div>
        <p className="text-[13px] text-[#64748B] -mt-1 mb-1 leading-relaxed">
          Use API keys to authenticate requests to the Help Genie API.
        </p>

        {isLoading ? (
          /* loading: skeleton key rows */
          <div className="flex flex-col">
            {[0, 1].map((i) => (
              <div
                key={i}
                className={`flex items-center gap-3.5 py-3.5 ${
                  i === 0 ? "" : "border-t border-genie-border/60"
                }`}
              >
                <div className="w-9 h-9 rounded-[10px] bg-gray-100 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-28 rounded bg-gray-100 animate-pulse" />
                  <div className="h-3 w-44 rounded bg-gray-100 animate-pulse" />
                </div>
                <div className="h-7 w-16 rounded-full bg-gray-100 animate-pulse" />
              </div>
            ))}
          </div>
        ) : activeKeys.length === 0 ? (
          /* purpose-built empty state + CTA */
          <div className="text-center py-8 mt-1 rounded-xl border border-dashed border-genie-border bg-genie-background">
            <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
              <Key className="w-5 h-5 text-genie-primary" />
            </div>
            <p className="text-[13.5px] font-bold text-[#222B45]">No API keys yet</p>
            <p className="text-[12.5px] text-[#64748B] mt-1 mb-4 px-6">
              Create a key to start making requests to the Help Genie API.
            </p>
            <div className="flex justify-center">
              <AcctButton
                label="Create your first key"
                kind="solid"
                icon="code"
                onClick={() => setCreateOpen(true)}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {activeKeys.map((key, i) => {
              const tint = keyTint(key);
              const masked = `hg_live_${key.key_prefix}••••••••••••`;
              const isRevoking = revokingId === key.id;
              return (
                <div
                  key={key.id}
                  className={`flex items-center gap-3.5 py-3.5 ${
                    i === 0 ? "" : "border-t border-genie-border/60"
                  }`}
                >
                  <div
                    className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${tint}1a` }}
                  >
                    <Key className="w-[17px] h-[17px]" style={{ color: tint }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-bold text-[#222B45] truncate">
                      {key.name}
                    </p>
                    <p
                      className="text-[12.5px] text-[#64748B] mt-0.5 truncate"
                      style={{ fontFamily: MONO }}
                    >
                      {masked}
                    </p>
                  </div>
                  <span className="text-[11.5px] text-gray-400 flex-shrink-0 hidden sm:inline">
                    Created {formatDate(key.created_at)}
                  </span>
                  <button
                    type="button"
                    onClick={() => copyValue(masked, key.id, "Key prefix")}
                    title="Copy key prefix"
                    className="w-8 h-8 rounded-[9px] border border-genie-border inline-flex items-center justify-center text-[#64748B] hover:border-genie-primary hover:text-genie-primary transition-colors flex-shrink-0"
                  >
                    {copiedId === key.id ? (
                      <Check className="w-[15px] h-[15px] text-genie-success" />
                    ) : (
                      <Copy className="w-[15px] h-[15px]" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRevoke(key)}
                    disabled={isRevoking}
                    className="h-7 px-3 rounded-full border border-genie-border inline-flex items-center gap-1.5 text-[11.5px] font-bold text-genie-secondary hover:bg-genie-secondary/5 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    {isRevoking && <Loader2 className="w-3 h-3 animate-spin" />}
                    {isRevoking ? "Revoking..." : "Revoke"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </AcctCard>

      {/* ── Webhooks + connected IDs / docs ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-5 items-start">
        {/* webhooks */}
        <AcctCard>
          <div className="flex items-center justify-between mb-3.5">
            <p className={SECTION_LABEL}>Webhooks</p>
            <AcctButton
              label="Add endpoint"
              kind="ghost"
              icon="code"
              onClick={() =>
                toast.success(
                  "Webhook endpoints are coming soon. We'll let you know the moment they're ready."
                )
              }
            />
          </div>

          {/* purpose-built empty state + CTA */}
          <div className="text-center py-8 rounded-xl border border-dashed border-genie-border bg-genie-background">
            <div className="w-11 h-11 mx-auto rounded-[13px] bg-genie-primary/10 flex items-center justify-center mb-3">
              <Webhook className="w-5 h-5 text-genie-primary" />
            </div>
            <p className="text-[13.5px] font-bold text-[#222B45]">
              No endpoints yet
            </p>
            <p className="text-[12.5px] text-[#64748B] mt-1 mb-4 px-6">
              Add an endpoint to receive events like completed calls and new
              messages in real time.
            </p>
            <div className="flex justify-center">
              <AcctButton
                label="Add your first endpoint"
                kind="tinted"
                icon="code"
                onClick={() =>
                  toast.success(
                    "Webhook endpoints are coming soon. We'll let you know the moment they're ready."
                  )
                }
              />
            </div>
          </div>
        </AcctCard>

        {/* connected IDs + docs */}
        <div className="flex flex-col gap-5">
          <AcctCard label="Connected IDs">
            <div className="flex flex-col gap-3">
              {connectedIds.map((id) => (
                <div key={id.copyId} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-[10px] bg-genie-background border border-genie-border flex items-center justify-center flex-shrink-0">
                    <Key className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11.5px] font-semibold text-[#64748B]">
                      {id.label}
                    </p>
                    <p
                      className="text-[13px] font-semibold text-[#222B45] truncate"
                      style={{ fontFamily: MONO }}
                    >
                      {id.value}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyValue(id.value, id.copyId, id.label)}
                    title={`Copy ${id.label}`}
                    disabled={id.value === "—"}
                    className="w-[30px] h-[30px] rounded-[8px] border border-genie-border inline-flex items-center justify-center text-[#64748B] hover:border-genie-primary hover:text-genie-primary transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {copiedId === id.copyId ? (
                      <Check className="w-[14px] h-[14px] text-genie-success" />
                    ) : (
                      <Copy className="w-[14px] h-[14px]" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </AcctCard>

          {/* docs box → API reference */}
          <a
            href={API_REFERENCE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group rounded-[18px] border border-genie-secondary/20 bg-genie-secondary/[0.04] px-5 py-[18px] flex items-center gap-3.5 transition-all hover:border-genie-secondary/40 hover:bg-genie-secondary/[0.07]"
          >
            <div className="w-10 h-10 rounded-[11px] bg-genie-secondary/15 flex items-center justify-center flex-shrink-0">
              <Code className="w-[19px] h-[19px] text-genie-secondary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-extrabold text-[#222B45]">
                API reference
              </p>
              <p className="text-[12px] text-[#64748B] mt-0.5">
                Quick-start, endpoints, and SDKs.
              </p>
            </div>
            <ArrowRight className="w-[18px] h-[18px] text-genie-secondary flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>

      {/* ── sheets (re-skin of DevelopersSection create / reveal flow) ── */}
      <DevelopersCreateKeySheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        creating={creating}
        onCreate={handleCreate}
      />
      <DevelopersRevealKeySheet
        apiKey={createdKey}
        onClose={() => setCreatedKey(null)}
      />
    </>
  );
};

export default DevelopersTab;
